import { ChangeDetectorRef, Component, effect, inject, input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { I18nModule, RoutingService } from '@spartacus/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { ProductUtils } from '../../utils/productUtils';
import { CustomPipesModule } from '../../pipes/custom-pipes.module';
import { CustomProduct, CustomProductApiParams } from '../../model/product.model';
import { PriceChannel } from '../../model/price-channel.model';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ContractConditions } from '../../model/contract-conditions.model';
import { CustomProdPgService } from '../../../custom-prod-pg/custom-prod-pg.service';
import { CartsApi } from '../../model/cart.model';
import { StockAvailableStatus } from '../../model/stock-available-status';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add-to-cart-button',
  imports: [
    I18nModule,
    NgIf,
    CustomPipesModule,
    NgForOf,
    NgbPopover,
    NgClass
  ],
  templateUrl: './add-to-cart-button.component.html'
})
export class AddToCartButtonComponent implements OnDestroy {
  protected readonly modal = inject(NgbModal);
  protected readonly plpService = inject(CustomProdPgService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly routingService = inject(RoutingService);

  private readonly successMessageTimeout = 2500;

  @ViewChild('p2') public successPopover: NgbPopover;

  protected readonly ProductUtils = ProductUtils;
  protected readonly PriceChannel = PriceChannel;
  protected readonly StockAvailableStatus = StockAvailableStatus;

  productInfo = input.required<CustomProduct>();
  selectedChannel = input.required<PriceChannel>();
  userDetails = input.required<any>();
  contractSelected = input.required<ContractConditions>();
  priceToShowGroupId = input.required<string>();
  contractSelectedId = input.required<string>();
  carts = input.required<CartsApi[]>();
  enableBtn = input.required<boolean>();
  fullWidthButton = input<boolean>(false);
  isQuickOrder = input<boolean>(false);

  panelVolumeExpanded = false;
  panelProductExpanded = false;
  itemAddedQuantity: string;
  flagRemoved = false;
  private successMsg = false;
  private cartId: string;
  private cartDetailsSub: Subscription;
  private updateErrorSub: Subscription;
  buttonName = 'Login to order';

  addToCart(productCode: string): void {
    this.successMsg = true;
    const inputElement = document.getElementById(`prodQuantity${productCode}${this.isQuickOrder()? 'q': ''}`) as HTMLInputElement;
    this.itemAddedQuantity = inputElement.value;
    //set params for private channel
    let params: CustomProductApiParams;
    const defaultParams = {
      product: { code: productCode },
      quantity: inputElement.value,
    };

    params = { ...defaultParams };

    //set params for public should include contractId
    if (this.selectedChannel() === PriceChannel.PUBLIC) {
      const extraParams = {
        contractId: this.contractSelected()?.contractId,
        contractName: this.contractSelected()?.contractName ? this.contractSelected()?.contractName : '',
        groupId: this.priceToShowGroupId(),
      };
      params = { ...defaultParams, ...extraParams };
    }

    // Check carts - get info then Update cart
    if (this.carts() && this.carts()?.length === 0) {
      this.plpService.createCart(this.userDetails()?.userName);
      this.plpService.createdCart$.subscribe((res) => {
        if (res) {
          this.cartId = res?.code;
          this.plpService.getCarts(this.userDetails()?.userName);
          this.updateCartDetails(params, this.cartId);
        }
      });
    } else {
      const cartId = this.carts()[0]?.code;
      this.updateCartDetails(params, cartId);
    }
  }

  openModal(content: TemplateRef<any>, prodCode: string): void {
    // check if there is contract already
    if (!!sessionStorage.getItem('contractFromCartApi') || this.selectedChannel() === PriceChannel.PRIVATE || !this.selectedChannel()) {
      this.addToCart(prodCode);
    } else {
      this.modal.open(content, {
        centered: true,
        backdrop: 'static',
      });
    }
  }

  goToLogin(): void {
    this.routingService.goByUrl('/login');
  }

  ngOnDestroy(): void {
    this.cartDetailsSub?.unsubscribe();
    this.updateErrorSub?.unsubscribe();
  }

  checkIfUnavailableToOrder(): boolean {
    return !this.checkIfProductIsLimited() && !this.productPriceIsValid() && !this.conditionsAndPriceDataValueIsValid();
  }

  checkIfProductCanBeAddedToTheCart(): boolean {
    return this.checkIfProductIsAvailable() &&
      !this.checkIfProductIsLimited() &&
      this.conditionsAndPriceDataValueIsValid();
  }

  checkIfProductCanBeAddedToTheCartWithPrivate(): boolean {
    return this.checkIfProductIsAvailable() &&
      !this.checkIfProductIsLimited() &&
      this.productPriceIsValid();
  }

  checkIfProductIsAvailable(): boolean {
    return !this.productInfo()?.discontinued && this.productInfo()?.available;
  }

  private checkIfProductIsLimited(): boolean {
    return this.productInfo()?.stockAvailableStatus === 'LIMITED' && this.productInfo()?.allowLimitedStockProducts !== true;
  }

  private conditionsAndPriceDataValueIsValid(): boolean {
    return this.productInfo()?.conditions &&
      !!this.productInfo()?.conditions.length &&
      !!this.productInfo()?.conditions[0]?.priceGroups[0]?.priceData.value &&
      this.selectedChannel() === PriceChannel.PUBLIC;
  }

  private productPriceIsValid(): boolean {
    return this.productInfo()?.price && !!this.productInfo()?.price.value && this.selectedChannel() === PriceChannel.PRIVATE;
  }

  private updateCartDetails(params: CustomProductApiParams, cartId: string): void {
    if (this.userDetails()?.uid && cartId) {
      this.plpService.updateCart(params, this.userDetails()?.uid, cartId);

      this.cartDetailsSub = this.plpService.cartDetailsUpdate$.subscribe(res => {
          if (!res) {
            this.cdr.detectChanges();
            return;
          }

          if (this.successMsg) {
            this.successPopover.open();
            setTimeout(() => this.successPopover.close(), this.successMessageTimeout);
            this.successMsg = false;
            if (this.selectedChannel() === PriceChannel.PUBLIC && !sessionStorage.getItem('contractFromCartApi')) {
              sessionStorage.setItem('contractFromCartApi', params?.contractId);
              window.location.reload();
            }
          }

          this.cdr.detectChanges();
        }
      );

      this.updateErrorSub = this.plpService.updateError$.subscribe((res) => {
        if (res) {
          this.successMsg = false;
        }
      });
    }
    this.cdr.detectChanges();
  }
}
